import { useState, useMemo, useEffect, useCallback } from "react";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import { useAssetConfig } from "@/hooks/useAssetConfig";
import api from "@/lib/api";
import { CheckCircle2, Search, Sparkles, X, MapPin, Upload } from "lucide-react";
import LiveFeed from "@/components/shared/LiveFeed";
import LiveMap from "@/components/shared/LiveMap";
import DataImport from "@/components/planner/DataImport";
import TutorialLedger from "@/components/shared/TutorialLedger";
import AnalyticsDashboard from "@/components/planner/AnalyticsDashboard";
import SubjectDetail from "@/components/planner/SubjectDetail";
import ActivityLogPanel from "@/components/shared/ActivityLogPanel";
import PigeonFAB from "@/components/shared/PigeonFAB";
import HubAdmin from "@/components/planner/HubAdmin";
import PlannerHeader from "@/components/planner/PlannerHeader";
import DriverReviewModal from "@/components/planner/DriverReviewModal";
import CalendarPanel from "@/components/planner/CalendarPanel";
import RouteBuilderPanel from "@/components/planner/RouteBuilderPanel";
import SiteCard from "@/components/planner/SiteCard";
import FleetSidebar from "@/components/planner/FleetSidebar";

const REGIONS = ["Antwerpen", "Brussel", "Oost-Vlaanderen", "West-Vlaanderen", "Wallonie"];

export default function PlannerDashboard() {
  const { session, hubData, refreshData, loading, auth } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const ac = useAssetConfig(session);
  const { sites, machines, drivers, requests, events, feedback } = hubData;

  const [activeRegion, setActiveRegion] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [reviewDriver, setReviewDriver] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showRouteBuilder, setShowRouteBuilder] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [routeSites, setRouteSites] = useState([]);
  const [routeDriver, setRouteDriver] = useState("");
  const [routeName, setRouteName] = useState("");
  const [calForm, setCalForm] = useState({ event_type: "APPOINTMENT", title: "", date: "", time: "", assigned_to: "", description: "" });
  const [calNotifCount, setCalNotifCount] = useState(0);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showHubAdmin, setShowHubAdmin] = useState(false);
  const [subjectDetail, setSubjectDetail] = useState(null);
  const [funMode, setFunMode] = useState(false);
  const [funText, setFunText] = useState("");
  const [showMap, setShowMap] = useState(false);

  const showToast = useCallback((msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    showToast(t("data_synced"));
    setIsRefreshing(false);
  };

  const loadCalendarAndRoutes = useCallback(async () => {
    try {
      const [cal, rts] = await Promise.all([
        api.getCalendarEvents(session.hubId),
        api.getRoutes(session.hubId),
      ]);
      setCalendarEvents(cal);
      setRoutes(rts);
      setCalNotifCount(cal.filter(e => e.event_type === "LEAVE_REQUEST" && e.status === "PENDING").length);
    } catch (e) { void 0; }
  }, [session?.hubId]);

  useEffect(() => { loadCalendarAndRoutes(); }, [loadCalendarAndRoutes]);

  const handleCreateCalEvent = async () => {
    if (!calForm.title.trim() || !calForm.date) return;
    await api.createCalendarEvent({ hub_id: session.hubId, ...calForm, created_by: session.userName || "Planner" });
    showToast("Event created!");
    setCalForm({ event_type: "APPOINTMENT", title: "", date: "", time: "", assigned_to: "", description: "" });
    loadCalendarAndRoutes();
  };

  const handleApproveLeave = async (eventId, status) => {
    await api.approveLeave(eventId, status);
    showToast(`Leave ${status.toLowerCase()}`);
    loadCalendarAndRoutes();
  };

  const handleDeleteCalEvent = async (eventId) => {
    await api.deleteCalendarEvent(eventId);
    showToast("Event deleted");
    loadCalendarAndRoutes();
  };

  const handleToolboxUpload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("hub_id", session.hubId);
    fd.append("title", file.name);
    fd.append("date", new Date().toISOString().split("T")[0]);
    fd.append("created_by", session.userName || "Planner");
    await api.uploadToolbox(fd);
    showToast("Toolbox uploaded!");
    loadCalendarAndRoutes();
  };

  const handleCreateRoute = async () => {
    if (!routeDriver || routeSites.length === 0) return;
    await api.createRoute({ hub_id: session.hubId, name: routeName || `Route ${new Date().toLocaleDateString()}`, driver_name: routeDriver, site_ids: routeSites });
    showToast("Route assigned!");
    setRouteSites([]); setRouteDriver(""); setRouteName("");
    setShowRouteBuilder(false);
    loadCalendarAndRoutes();
  };

  const handleDeleteRoute = async (routeId) => {
    await api.deleteRoute(routeId);
    showToast("Route deleted");
    loadCalendarAndRoutes();
  };

  const handleFunRequest = async () => {
    if (!funText.trim()) return;
    await api.createRequest({
      hub_id: session.hubId, machine_id: "FUN", machine_number: "FUN-001",
      site_id: sites[0]?.id || "FUN", site_name: "Fun Request",
      urgency: "NONE", products: ["FUN"], notes: funText,
      requester_type: "PLANNER", is_fun_request: true, fun_message: funText
    });
    showToast("Fun request sent!");
    setFunText(""); setFunMode(false);
    refreshData();
  };

  // PDF generation via jsPDF
  const generatePDF = async (type, subject) => {
    const { jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const doc = new jsPDF();
    const now = new Date().toLocaleDateString();
    const hubName = session?.hubName || "Hub";
    doc.setFontSize(18); doc.setTextColor(30);

    if (type === "site-log" && subject) {
      doc.text(`Site Log: ${subject.name}`, 14, 22);
      doc.setFontSize(9); doc.setTextColor(120);
      doc.text(`${subject.city} / ${subject.region} | ${now} | Pigeon Supply`, 14, 30);
      const siteMachines = machines.filter(m => m.site_id === subject.id);
      const siteReqs = requests.filter(r => r.site_id === subject.id);
      const siteEvents = events.filter(e => e.site_id === subject.id).slice(0, 50);
      autoTable(doc, { startY: 44, head: [["Machine #", "Name", "Brand", "Status", "Fuel %"]], body: siteMachines.map(m => [m.machine_number, m.name, m.brand || "-", m.status_type, `${m.fuel_level_percent}%`]), styles: { fontSize: 8 }, headStyles: { fillColor: [59, 130, 246] } });
      let y = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11); doc.setTextColor(30); doc.text("Requests", 14, y); y += 6;
      autoTable(doc, { startY: y, head: [["Type", "Machine", "Status", "Urgency", "Date"]], body: siteReqs.map(r => [r.request_type, r.machine_number, r.status, r.urgency, new Date(r.created_at).toLocaleDateString()]), styles: { fontSize: 7 }, headStyles: { fillColor: [132, 204, 22] } });
      y = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11); doc.setTextColor(30); doc.text("Activity Log", 14, y); y += 6;
      autoTable(doc, { startY: y, head: [["Time", "Event", "Message"]], body: siteEvents.map(e => [new Date(e.timestamp).toLocaleString(), e.event_type, e.message?.substring(0, 80)]), styles: { fontSize: 7 }, headStyles: { fillColor: [100, 116, 139] } });
      doc.save(`Site_${subject.name}_log_${now.replace(/\//g, "-")}.pdf`);
      showToast("Site log exported!"); return;
    }
    if (type === "machine-log" && subject) {
      const mSite = sites.find(s => s.id === subject.site_id);
      doc.text(`Machine Log: ${subject.machine_number}`, 14, 22);
      doc.setFontSize(9); doc.setTextColor(120);
      doc.text(`${subject.name} | ${subject.brand || "N/A"} | Site: ${mSite?.name || "N/A"} | ${now}`, 14, 30);
      const mReqs = requests.filter(r => r.machine_id === subject.id);
      const mEvents = events.filter(e => e.machine_id === subject.id || e.machine_number === subject.machine_number).slice(0, 50);
      autoTable(doc, { startY: 44, head: [["Type", "Status", "Urgency", "Notes", "Date"]], body: mReqs.map(r => [r.request_type, r.status, r.urgency, r.notes?.substring(0, 60) || "-", new Date(r.created_at).toLocaleDateString()]), styles: { fontSize: 8 }, headStyles: { fillColor: [245, 158, 11] } });
      let y = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11); doc.setTextColor(30); doc.text("Activity Log", 14, y); y += 6;
      autoTable(doc, { startY: y, head: [["Time", "Event", "Message"]], body: mEvents.map(e => [new Date(e.timestamp).toLocaleString(), e.event_type, e.message?.substring(0, 80)]), styles: { fontSize: 7 }, headStyles: { fillColor: [100, 116, 139] } });
      doc.save(`Machine_${subject.machine_number}_log_${now.replace(/\//g, "-")}.pdf`);
      showToast("Machine log exported!"); return;
    }
    if (type === "driver-log" && subject) {
      const stats = driverStats[subject] || {};
      const driverFb = (feedback || []).filter(f => f.driver_name === subject);
      const driverEvents = events.filter(e => e.driver_name === subject).slice(0, 50);
      doc.text(`Driver Log: ${subject}`, 14, 22);
      doc.setFontSize(9); doc.setTextColor(120);
      doc.text(`${now} | Pigeon Supply`, 14, 30);
      doc.text(`Diesel: ${stats.liters || 0}L | Tasks: ${stats.machines || 0} | AdBlue: ${stats.adblue || 0}L`, 14, 36);
      autoTable(doc, { startY: 44, head: [["Time", "Event", "Message"]], body: driverEvents.map(e => [new Date(e.timestamp).toLocaleString(), e.event_type, e.message?.substring(0, 80)]), styles: { fontSize: 7 }, headStyles: { fillColor: [239, 68, 68] } });
      if (driverFb.length > 0) {
        let y = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(11); doc.setTextColor(30); doc.text("Feedback", 14, y); y += 6;
        autoTable(doc, { startY: y, head: [["Type", "Incident", "Site", "Text"]], body: driverFb.map(f => [f.type, f.incident_type || "-", f.site_name || "-", f.text?.substring(0, 60) || "-"]), styles: { fontSize: 7 }, headStyles: { fillColor: [132, 204, 22] } });
      }
      doc.save(`Driver_${subject}_log_${now.replace(/\//g, "-")}.pdf`);
      showToast("Driver log exported!"); return;
    }

    doc.text(`${hubName} - ${type.toUpperCase()} LOG`, 14, 22);
    doc.setFontSize(9); doc.setTextColor(120);
    doc.text(`Generated: ${now} | Pigeon Supply`, 14, 30);
    if (type === "sites") {
      autoTable(doc, { startY: 38, head: [["Code", "Name", "City", "Region", "GPS", "Machines", "Open Requests"]], body: sites.map(s => [s.code, s.name, s.city, s.region, s.gps_lat > 0 ? `${s.gps_lat.toFixed(3)},${s.gps_lon.toFixed(3)}` : "-", machines.filter(m => m.site_id === s.id).length, requests.filter(r => r.site_id === s.id && r.status !== "DONE").length]), styles: { fontSize: 8, cellPadding: 3 }, headStyles: { fillColor: [132, 204, 22] } });
    } else if (type === "machines") {
      autoTable(doc, { startY: 38, head: [["Number", "Name", "Site", "Status", "Brand", "Fuel%", "GPS"]], body: machines.map(m => { const site = sites.find(s => s.id === m.site_id); return [m.machine_number, m.name, site?.name || "-", m.status_type || "OK", m.brand || "-", `${m.fuel_level_percent}%`, m.gps_lat > 0 ? `${m.gps_lat.toFixed(3)},${m.gps_lon.toFixed(3)}` : "-"]; }), styles: { fontSize: 8, cellPadding: 3 }, headStyles: { fillColor: [132, 204, 22] } });
    } else if (type === "drivers") {
      autoTable(doc, { startY: 38, head: [["Name", "Region", "Status", "High Fives", "Diesel Delivered", "Tasks Done"]], body: drivers.map(d => { const stats = driverStats[d.driver_name] || {}; return [d.driver_name, d.region, d.status, d.high_five_count || 0, `${stats.liters || 0}L`, stats.machines || 0]; }), styles: { fontSize: 8, cellPadding: 3 }, headStyles: { fillColor: [132, 204, 22] } });
    }
    doc.save(`${hubName}_${type}_${now.replace(/\//g, "-")}.pdf`);
    showToast(`${type} PDF exported!`);
  };

  const driverStats = useMemo(() => {
    const stats = {};
    drivers.forEach(d => {
      const dReqs = requests.filter(r => r.assigned_driver === d.driver_name && r.status === "DONE");
      const total = requests.filter(r => r.assigned_driver === d.driver_name).length;
      stats[d.driver_name] = {
        liters: dReqs.reduce((s, r) => s + (r.delivered_diesel || 0), 0),
        adblue: dReqs.reduce((s, r) => s + (r.delivered_adblue || 0), 0),
        machines: dReqs.length,
        sites: new Set(dReqs.map(r => r.site_id)).size,
        efficiency: total > 0 ? Math.round((dReqs.length / total) * 100) : 0,
        tasks: dReqs.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
      };
    });
    return stats;
  }, [drivers, requests]);

  const filteredSites = useMemo(() => {
    return sites.filter(s => {
      const matchRegion = activeRegion === "ALL" || s.region === activeRegion;
      const matchSearch = (s.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (s.city || "").toLowerCase().includes(searchQuery.toLowerCase());
      return matchRegion && matchSearch;
    });
  }, [sites, activeRegion, searchQuery]);

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col" data-testid="planner-dashboard">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[1000] bg-lime-500 text-slate-950 px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown">
          <CheckCircle2 className="w-4 h-4" /> {toast}
        </div>
      )}

      <TutorialLedger role="planner" isOpen={showTutorial} onClose={() => setShowTutorial(false)} />
      {showAnalytics && <AnalyticsDashboard hubId={session.hubId} session={session} onClose={() => setShowAnalytics(false)} />}
      {showHubAdmin && <HubAdmin hubId={session.hubId} onClose={() => setShowHubAdmin(false)} />}

      {subjectDetail && (
        <SubjectDetail type={subjectDetail.type} subject={subjectDetail.subject}
          hubData={{ sites, machines, requests, events, drivers, feedback }}
          onClose={() => setSubjectDetail(null)}
          onExportPDF={() => {
            if (subjectDetail.type === "site") generatePDF("site-log", subjectDetail.subject);
            if (subjectDetail.type === "machine") generatePDF("machine-log", subjectDetail.subject);
            if (subjectDetail.type === "driver") generatePDF("driver-log", subjectDetail.subject);
          }}
        />
      )}

      {reviewDriver && (
        <DriverReviewModal driver={reviewDriver} driverStats={driverStats}
          onClose={() => setReviewDriver(null)} onSubjectDetail={setSubjectDetail} />
      )}

      <PlannerHeader
        isRefreshing={isRefreshing} onRefresh={handleRefresh} calNotifCount={calNotifCount}
        showImport={showImport} setShowImport={setShowImport}
        showCalendar={showCalendar} setShowCalendar={setShowCalendar}
        showRouteBuilder={showRouteBuilder} setShowRouteBuilder={setShowRouteBuilder}
        showMap={showMap} setShowMap={setShowMap}
        funMode={funMode} setFunMode={setFunMode}
        setShowAnalytics={setShowAnalytics} setShowHubAdmin={setShowHubAdmin}
        setShowTutorial={setShowTutorial} showTutorial={showTutorial}
        loadCalendarAndRoutes={loadCalendarAndRoutes} generatePDF={generatePDF}
      />

      {/* Fun Request Bar */}
      {funMode && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 md:px-8 py-3 flex items-center gap-3 animate-slideInDown">
          <Sparkles className="w-5 h-5 text-amber-500 shrink-0" />
          <input data-testid="fun-request-input" value={funText} onChange={e => setFunText(e.target.value)}
            placeholder={t("fun_placeholder")} className="flex-1 bg-transparent text-sm text-slate-900 font-bold placeholder:text-amber-500/50 outline-none" />
          <button data-testid="send-fun-request-btn" onClick={handleFunRequest} disabled={!funText.trim()}
            className="px-4 py-2 bg-amber-500 text-slate-950 rounded-xl text-xs font-bold uppercase disabled:opacity-30 active:scale-95 transition-all">Send</button>
        </div>
      )}

      {/* Map */}
      {showMap && (
        <div className="px-4 md:px-8 py-4 bg-slate-50/80 border-b border-slate-200 animate-slideInDown" data-testid="planner-map-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-blue-500" /> Site & Asset Map</h3>
            <button onClick={() => setShowMap(false)} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm"><LiveMap sites={sites} machines={machines} requests={requests} /></div>
        </div>
      )}

      {/* Import Panel */}
      {showImport && (
        <div className="bg-slate-50/80 border-b border-slate-200 px-4 md:px-8 py-4 animate-slideInDown" data-testid="import-panel">
          <div className="max-w-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><Upload className="w-4 h-4 text-lime-500" /> Philogic Data Import</h3>
              <button onClick={() => setShowImport(false)} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
            </div>
            <DataImport hubId={session?.hubId} onImportComplete={() => { refreshData(); showToast("Data imported to hub!"); }} />
          </div>
        </div>
      )}

      {showCalendar && (
        <CalendarPanel calendarEvents={calendarEvents} calForm={calForm} setCalForm={setCalForm}
          onClose={() => setShowCalendar(false)} onCreateEvent={handleCreateCalEvent}
          onApproveLeave={handleApproveLeave} onDeleteEvent={handleDeleteCalEvent}
          onToolboxUpload={handleToolboxUpload} />
      )}

      {showRouteBuilder && (
        <RouteBuilderPanel routes={routes} routeSites={routeSites} setRouteSites={setRouteSites}
          routeDriver={routeDriver} setRouteDriver={setRouteDriver}
          routeName={routeName} setRouteName={setRouteName}
          onCreateRoute={handleCreateRoute} onDeleteRoute={handleDeleteRoute}
          onClose={() => setShowRouteBuilder(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 grid grid-cols-1 lg:grid-cols-4 gap-6 overflow-hidden">
        <div className="lg:col-span-3 space-y-5 overflow-y-auto pr-1 max-h-[calc(100vh-160px)]">
          <LiveFeed events={events} />

          {/* Region Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
            <button data-testid="region-all" onClick={() => setActiveRegion("ALL")}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${activeRegion === "ALL" ? "bg-lime-500 text-slate-950 border-lime-500" : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300"}`}>
              {t("all_regions")}
            </button>
            {REGIONS.map(r => (
              <button key={r} data-testid={`region-${r}`} onClick={() => setActiveRegion(r)}
                className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${activeRegion === r ? "bg-lime-500 text-slate-950 border-lime-500" : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300"}`}>
                {r}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="planner-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-lime-400 outline-none transition-all" />
          </div>

          {/* Sites */}
          <div className="space-y-3">
            {filteredSites.map((site, i) => (
              <SiteCard key={site.id} site={site} index={i} onSubjectDetail={setSubjectDetail} showToast={showToast} />
            ))}
          </div>
        </div>

        <FleetSidebar driverStats={driverStats} onReviewDriver={setReviewDriver} />

        <div className="col-span-full mt-6" data-testid="planner-activity-section">
          <ActivityLogPanel hubId={session?.hubId} />
        </div>
      </main>

      <PigeonFAB role="planner" events={events} />
    </div>
  );
}
